import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The one error boundary implementation in the application.
 *
 * React has no hook form of this -- a boundary must be a class -- so the choice
 * is one shared class or a copy per use site. There was previously exactly one
 * boundary in the entire frontend, private to `component-frame.tsx` and
 * wrapping dashboard tiles only. Everything else was unprotected: a throw in
 * the Gantt renderer, the cost charts, the MapLibre canvas or the task panel
 * unmounted the whole React tree to a blank page, with no reload affordance and
 * nothing reported anywhere.
 *
 * That is the worst failure mode a frontend has, and it was most likely in
 * precisely the two most complex surfaces the product has.
 *
 * `resetKey` is what makes recovery possible. A boundary that has caught stays
 * caught until it is remounted, so a route-level boundary with no reset would
 * turn one bad render into a page the user cannot navigate away from. Passing
 * the current location clears the error on navigation, which is the behaviour a
 * reader expects: the broken screen is broken, the next one is not.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (error: unknown, reset: () => void) => ReactNode;
  /** Changing this clears a caught error. Usually the current route. */
  resetKey?: string;
  /** Identifies the boundary in the telemetry line below. */
  label?: string;
}

interface ErrorBoundaryState {
  error: unknown;
  resetKey?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error };
  }

  /**
   * Clears the caught error when the reset key changes.
   *
   * In `getDerivedStateFromProps` rather than an effect: an effect does not run
   * on a render that produced the fallback, so the error would survive the very
   * navigation meant to escape it.
   */
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Frontend error telemetry (section 19.1). A real deployment forwards this
    // to the collector; the console keeps it visible in development.
    console.error('Unhandled render error', {
      boundary: this.props.label ?? 'unlabelled',
      error,
      componentStack: info.componentStack,
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) return this.props.fallback(this.state.error, this.reset);
    return this.props.children;
  }
}
